/**
 * WindMap Component
 * 
 * A dedicated component for rendering wind renewable energy potential data
 * (both onshore and offshore) on an interactive Leaflet map.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import L from 'leaflet';
import { createMap, calculateMapBounds, fitMapBoundsAsHome } from '../../utils/mapUtils';
import { 
  optimizeGeoJSONData,
  buildRadialFillGeoJSON,
  getRadialFillStyle,
  RADIAL_OUTLINE_STYLE,
  createZonePopup, 
  getZoneStyle, 
  getHoverStyle
} from '../../utils/renewableUtils';

const WindMap = ({ 
  onshoreWindData, 
  offshoreWindData,
  onshoreWindThresholds, 
  offshoreWindThresholds,
  showOnshoreLayer = true,
  showOffshoreLayer = true,
  countryIso,
  className = "w-full h-full",
  layoutActive = true,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  // Tracks the dataset composition we last fit bounds for. Format:
  //   "<countryIso>|<onshore?>|<offshore?>" e.g. "USA|1|0", "USA|1|1".
  // The fit is re-run whenever this signature changes — so country switches
  // refit, AND a country whose offshore arrives after onshore was already fit
  // will refit once to encompass both datasets.
  const lastFitSignatureRef = useRef(undefined);
  const boundsFitSessionRef = useRef(0);

  const clearWindMapLayers = useCallback((map) => {
    if (!map) return;
    const windLayersToRemove = [];
    map.eachLayer((layer) => {
      if (
        layer instanceof L.GeoJSON &&
        (layer.options.onshoreWindLayer ||
          layer.options.offshoreWindLayer ||
          layer.options.onshoreWindOutlineLayer ||
          layer.options.offshoreWindOutlineLayer)
      ) {
        windLayersToRemove.push(layer);
      }
    });
    if (windLayersToRemove.length > 0) {
      if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
      windLayersToRemove.forEach((layer) => map.removeLayer(layer));
    }
    map.options.boundsSet = false;
  }, []);

  // Create GeoJSON layer helper
  const createGeoJSONLayer = useCallback((geoJsonData, styleFunction, onEachFeature) => {
    const layer = L.geoJSON(geoJsonData, {
      style: styleFunction,
      onEachFeature: onEachFeature
    });
    
    return layer;
  }, []);

  // Render wind zone shapes - thresholds passed as parameters, not in dependencies
  const renderWindZoneShapes = useCallback((map, onshoreData, offshoreData, onshoreThresholds, offshoreThresholds) => {
    if (!map) {
      return;
    }
    
    // Handle empty data
    if ((!onshoreData || !onshoreData.grid_data || onshoreData.grid_data.length === 0) && 
        (!offshoreData || !offshoreData.grid_data || offshoreData.grid_data.length === 0)) {
      // Clear existing layers if no data
      map.eachLayer((layer) => {
        if (layer instanceof L.CircleMarker || layer instanceof L.GeoJSON) {
          map.removeLayer(layer);
        }
      });
      return;
    }

    // Remove existing layers before creating new ones (fill + optional outline layers)
    const windLayersToRemove = [];
    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON && (
        layer.options.onshoreWindLayer ||
        layer.options.offshoreWindLayer ||
        layer.options.onshoreWindOutlineLayer ||
        layer.options.offshoreWindOutlineLayer
      )) {
        windLayersToRemove.push(layer);
      }
    });
    if (windLayersToRemove.length > 0) {
      if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
      windLayersToRemove.forEach((l) => map.removeLayer(l));
    }

    // Get current map bounds and zoom level for performance optimization
    const mapBounds = map.getBounds();
    const zoomLevel = map.getZoom();

    // Render onshore wind shapes
    if (showOnshoreLayer && onshoreData && onshoreData.grid_data && onshoreData.grid_data.length > 0 && onshoreThresholds) {
      const onshoreZones = onshoreData.grid_data;

      if (onshoreThresholds?.mode === 'continuous') {
        // Radial-fill path
        const { outlines, fills } = buildRadialFillGeoJSON(onshoreZones);
        if (fills.features.length > 0) {
          const onshoreOutlineLayer = L.geoJSON(outlines, {
            style: () => RADIAL_OUTLINE_STYLE,
            interactive: false,
          });
          onshoreOutlineLayer.options.onshoreWindOutlineLayer = true;
          onshoreOutlineLayer.addTo(map);

          const onshoreLayer = createGeoJSONLayer(
            fills,
            (feature) => getRadialFillStyle(feature, onshoreThresholds),
            (feature, layer) => {
              layer.feature = feature;
              const zone = feature.properties;
              const tooltipContent = createZonePopup(zone, 'onshore_wind');

              layer.on('mouseover', (e) => {
                e.target.setStyle({ ...getHoverStyle(zoomLevel), fillOpacity: 0.95 });
                e.target.bringToFront();
                if (map._sharedTooltip) {
                  map._sharedTooltip.setLatLng(e.latlng).setContent(tooltipContent).addTo(map);
                }
              });
              layer.on('mousemove', (e) => {
                if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
              });
              layer.on('mouseout', (e) => {
                if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
                e.target.setStyle(getRadialFillStyle(feature, onshoreThresholds));
              });
            }
          );
          onshoreLayer.options.onshoreWindLayer = true;
          onshoreLayer.options.lastZoom = map.getZoom();
          onshoreLayer.addTo(map);
        }
      } else {
        // Standard path
        const onshoreGeoJsonData = optimizeGeoJSONData(onshoreZones, mapBounds, zoomLevel);
        if (onshoreGeoJsonData.features.length > 0) {
          const onshoreLayer = createGeoJSONLayer(
            onshoreGeoJsonData,
            (feature) => getZoneStyle(feature, 'onshore_wind', onshoreThresholds, zoomLevel),
            (feature, layer) => {
              const zone = feature.properties;
              layer.feature = feature;
              const tooltipContent = createZonePopup(zone, 'onshore_wind');

              layer.on('mouseover', (e) => {
                e.target.setStyle(getHoverStyle(zoomLevel));
                e.target.bringToFront();
                if (map._sharedTooltip) {
                  map._sharedTooltip.setLatLng(e.latlng).setContent(tooltipContent).addTo(map);
                }
              });
              layer.on('mousemove', (e) => {
                if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
              });
              layer.on('mouseout', (e) => {
                if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
                e.target.setStyle({
                  ...getZoneStyle(feature, 'onshore_wind', onshoreThresholds, zoomLevel),
                  weight: zoomLevel <= 4 ? 0.5 : zoomLevel <= 6 ? 0.8 : zoomLevel <= 8 ? 1 : 1.2,
                  opacity: 0.8,
                  fillOpacity: 0.6,
                  color: 'white',
                  dashArray: '3',
                });
              });
            }
          );
          onshoreLayer.options.onshoreWindLayer = true;
          onshoreLayer.options.lastZoom = map.getZoom();
          onshoreLayer.options.thresholds = onshoreThresholds;
          onshoreLayer.addTo(map);
        }
      }
    }

    // Render offshore wind shapes
    if (showOffshoreLayer && offshoreData && offshoreData.grid_data && offshoreData.grid_data.length > 0 && offshoreThresholds) {
      const offshoreZones = offshoreData.grid_data;

      if (offshoreThresholds?.mode === 'continuous') {
        // Radial-fill path
        const { outlines, fills } = buildRadialFillGeoJSON(offshoreZones);
        if (fills.features.length > 0) {
          const offshoreOutlineLayer = L.geoJSON(outlines, {
            style: () => RADIAL_OUTLINE_STYLE,
            interactive: false,
          });
          offshoreOutlineLayer.options.offshoreWindOutlineLayer = true;
          offshoreOutlineLayer.addTo(map);

          const offshoreLayer = createGeoJSONLayer(
            fills,
            (feature) => getRadialFillStyle(feature, offshoreThresholds),
            (feature, layer) => {
              layer.feature = feature;
              const zone = feature.properties;
              const tooltipContent = createZonePopup(zone, 'offshore_wind');

              layer.on('mouseover', (e) => {
                e.target.setStyle({ ...getHoverStyle(zoomLevel), fillOpacity: 0.95 });
                e.target.bringToFront();
                if (map._sharedTooltip) {
                  map._sharedTooltip.setLatLng(e.latlng).setContent(tooltipContent).addTo(map);
                }
              });
              layer.on('mousemove', (e) => {
                if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
              });
              layer.on('mouseout', (e) => {
                if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
                e.target.setStyle(getRadialFillStyle(feature, offshoreThresholds));
              });
            }
          );
          offshoreLayer.options.offshoreWindLayer = true;
          offshoreLayer.options.lastZoom = map.getZoom();
          offshoreLayer.addTo(map);
        }
      } else {
        // Standard path
        const offshoreGeoJsonData = optimizeGeoJSONData(offshoreZones, mapBounds, zoomLevel);
        if (offshoreGeoJsonData.features.length > 0) {
          const offshoreLayer = createGeoJSONLayer(
            offshoreGeoJsonData,
            (feature) => getZoneStyle(feature, 'offshore_wind', offshoreThresholds, zoomLevel),
            (feature, layer) => {
              const zone = feature.properties;
              layer.feature = feature;
              const tooltipContent = createZonePopup(zone, 'offshore_wind');

              layer.on('mouseover', (e) => {
                e.target.setStyle(getHoverStyle(zoomLevel));
                e.target.bringToFront();
                if (map._sharedTooltip) {
                  map._sharedTooltip.setLatLng(e.latlng).setContent(tooltipContent).addTo(map);
                }
              });
              layer.on('mousemove', (e) => {
                if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
              });
              layer.on('mouseout', (e) => {
                if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
                e.target.setStyle({
                  ...getZoneStyle(feature, 'offshore_wind', offshoreThresholds, zoomLevel),
                  weight: zoomLevel <= 4 ? 0.5 : zoomLevel <= 6 ? 0.8 : zoomLevel <= 8 ? 1 : 1.2,
                  opacity: 0.8,
                  fillOpacity: 0.6,
                  color: 'white',
                  dashArray: '3',
                });
              });
            }
          );
          offshoreLayer.options.offshoreWindLayer = true;
          offshoreLayer.options.lastZoom = map.getZoom();
          offshoreLayer.options.thresholds = offshoreThresholds;
          offshoreLayer.addTo(map);
        }
      }
    }
  }, [createGeoJSONLayer, showOnshoreLayer, showOffshoreLayer]); // Removed onZoneSelect and thresholds from dependencies

  // Initialize map immediately when component mounts (no dependencies)
  useEffect(() => {
    // Check if map already exists (from previous mount or external source)
    if (mapInstanceRef.current) {
      setMapReady(true);
      return;
    }
    
    if (!mapRef.current) {
      return;
    }
    
    // Wait for container to be ready and have dimensions
    let retryCount = 0;
    const maxRetries = 20; // Increased retries
    
    const initMap = () => {
      // Check if map was already created (by another effect run)
      if (mapInstanceRef.current) {
        setMapReady(true);
        return;
      }
      
      if (!mapRef.current) return;
      const container = mapRef.current;
      
      // Check if container has dimensions
      const hasDimensions = container.offsetWidth > 0 && container.offsetHeight > 0;
      if (!hasDimensions && retryCount < maxRetries) {
        retryCount++;
        // Retry on next frame if container doesn't have dimensions yet
        requestAnimationFrame(initMap);
        return;
      }
      
      // Create map even if dimensions check failed (after max retries)
      try {
        const map = createMap(container, {
          defaultStyle: 'CLEAN_MINIMAL',
          attributionControl: false
        });
        
        // Set initial view immediately (required before map can be used)
        map.setView([0, 0], 2, { animate: false });

        // Single shared tooltip for the whole map. Both onshore and offshore
        // feature handlers reuse this instance so only one tooltip is ever
        // visible — even at coastline zones where polygons overlap.
        map._sharedTooltip = L.tooltip({
          sticky: true,
          direction: 'auto',
          offset: [15, -15],
          className: 'renewable-zone-tooltip'
        });

        mapInstanceRef.current = map;
        setMapReady(true); // Signal that map is ready
      } catch (error) {
        console.error('Error creating wind map:', error);
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(initMap);
    
    // Cleanup only on unmount - actually remove the map
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current._vsMapInteraction?.destroy();
          mapInstanceRef.current._vsMapReset?.destroy();
          mapInstanceRef.current.stop();
          mapInstanceRef.current.remove();
        } catch (err) {
          console.warn('Error removing wind map:', err);
        }
        mapInstanceRef.current = null;
        setMapReady(false);
      }
    };
  }, []); // Empty deps - runs once on mount

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    clearWindMapLayers(mapInstanceRef.current);
    lastFitSignatureRef.current = undefined;
  }, [countryIso, clearWindMapLayers]);

  // Render layers only when BOTH data AND thresholds are ready (for each wind type)
  useEffect(() => {
    const session = ++boundsFitSessionRef.current;

    // Wait for map to be ready
    if (!mapReady || !mapInstanceRef.current) {
      return;
    }
    
    // Check if we have at least one wind type with both data and thresholds ready
    const onshoreReady = onshoreWindData?.grid_data?.length > 0 && onshoreWindThresholds;
    const offshoreReady = offshoreWindData?.grid_data?.length > 0 && offshoreWindThresholds;
    const visibleOnshoreReady = Boolean(showOnshoreLayer && onshoreReady);
    const visibleOffshoreReady = Boolean(showOffshoreLayer && offshoreReady);
    
    if (!visibleOnshoreReady && !visibleOffshoreReady) {
      clearWindMapLayers(mapInstanceRef.current);
      return;
    }
    
    // Wait for map to be loaded before rendering
    const renderLayers = () => {
      if (!mapInstanceRef.current) {
        return;
      }
      
      const map = mapInstanceRef.current;
      
      // Check if map is ready - try to get center, if it fails, wait
      let isMapReady = false;
      try {
        const center = map.getCenter();
        isMapReady = center && typeof center.lat === 'number' && typeof center.lng === 'number';
      } catch (error) {
        isMapReady = false;
      }
      
      if (!isMapReady) {
        // Map not ready yet, wait for it
        if (map.whenReady) {
          map.whenReady(() => {
            setTimeout(() => {
              renderLayers();
            }, 50);
          });
        } else {
          // Retry after a short delay
          setTimeout(() => {
            renderLayers();
          }, 100);
        }
        return;
      }

      const fitSignature = `${countryIso}|${visibleOnshoreReady ? '1' : '0'}|${visibleOffshoreReady ? '1' : '0'}`;
      if (fitSignature !== lastFitSignatureRef.current) {
        map.options.boundsSet = false;
      }
      
      // Render layers first (so they're visible)
      renderWindZoneShapes(
        map, 
        visibleOnshoreReady ? onshoreWindData : null,
        visibleOffshoreReady ? offshoreWindData : null,
        onshoreWindThresholds,
        offshoreWindThresholds
      );
      
      // Fit bounds AFTER rendering (so layers are visible)
      if (!map.options.boundsSet) {
        // Calculate bounds from ALL available data for initial view
        let coordinates = [];
        const extractCoordinates = (zone) => {
          if (!zone) {
            return;
          }

          const geometry = zone.geometry;
          // Prefer full geometry bounds for large/disjoint countries (e.g., USA)
          if (geometry && geometry.type && geometry.coordinates) {
            if (geometry.type === 'Polygon') {
              geometry.coordinates.forEach((ring) => {
                ring.forEach((coord) => {
                  coordinates.push([coord[1], coord[0]]); // lat, lng
                });
              });
              return;
            }

            if (geometry.type === 'MultiPolygon') {
              geometry.coordinates.forEach((polygon) => {
                polygon.forEach((ring) => {
                  ring.forEach((coord) => {
                    coordinates.push([coord[1], coord[0]]); // lat, lng
                  });
                });
              });
              return;
            }
          }

          if (zone.lat && zone.lng) {
            coordinates.push([zone.lat, zone.lng]);
          }
        };
        
        if (visibleOnshoreReady && onshoreWindData?.grid_data?.length > 0) {
          onshoreWindData.grid_data.forEach(extractCoordinates);
        }
        if (visibleOffshoreReady && offshoreWindData?.grid_data?.length > 0) {
          offshoreWindData.grid_data.forEach(extractCoordinates);
        }
        
        // Fit bounds if we have coordinates
        if (coordinates.length > 0) {
          try {
            const bounds = calculateMapBounds(coordinates, 0.1);
            // Wait for map to be fully ready before fitting bounds
            let retryCount = 0;
            const maxRetries = 30; // Maximum 3 seconds (30 * 100ms)
            
            const fitBoundsWhenReady = () => {
              if (session !== boundsFitSessionRef.current) {
                return;
              }
              if (retryCount >= maxRetries) {
                console.warn('Max retries reached for fitBounds, map may not be ready');
                return;
              }
              
              if (!map || !map._loaded || !map._panes || !map._panes.mapPane) {
                // Map not ready yet, retry
                retryCount++;
                setTimeout(fitBoundsWhenReady, 100);
                return;
              }
              
              try {
                // Check if map has a valid center (map is initialized)
                const center = map.getCenter();
                if (center && center.lat !== undefined && center.lng !== undefined) {
                  if (session !== boundsFitSessionRef.current) {
                    return;
                  }
                  fitMapBoundsAsHome(map, bounds, { padding: [20, 20], animate: false });
                  map.options.boundsSet = true;
                  lastFitSignatureRef.current = fitSignature;
                } else {
                  // Map center not set yet, retry
                  retryCount++;
                  setTimeout(fitBoundsWhenReady, 100);
                }
              } catch (error) {
                // Map not ready, retry
                retryCount++;
                if (retryCount < maxRetries) {
                  setTimeout(fitBoundsWhenReady, 100);
                } else {
                  console.warn('Error fitting bounds after retries:', error);
                }
              }
            };
            
            // Start checking after a small delay to let layers render
            setTimeout(fitBoundsWhenReady, 200);
          } catch (error) {
            console.warn('Error fitting bounds:', error);
          }
        }
      }
    };
    
    // Use a small delay to ensure map is initialized
    const timer = setTimeout(() => {
      renderLayers();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [
    onshoreWindData,
    offshoreWindData,
    onshoreWindThresholds,
    offshoreWindThresholds,
    renderWindZoneShapes,
    mapReady,
    countryIso,
    showOnshoreLayer,
    showOffshoreLayer
  ]);

  // Update layer colors when thresholds change (don't recreate layers)
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    const zoomLevel = mapInstanceRef.current.getZoom();
    
    // Update onshore layer colors
    if (onshoreWindThresholds) {
      let onshoreLayer = null;
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.GeoJSON && layer.options.onshoreWindLayer) {
          onshoreLayer = layer;
        }
      });
      
      if (onshoreLayer) {
        onshoreLayer.eachLayer((featureLayer) => {
          if (featureLayer.feature) {
            const newStyle = getZoneStyle(featureLayer.feature, 'onshore_wind', onshoreWindThresholds, zoomLevel);
            featureLayer.setStyle(newStyle);
          }
        });
        onshoreLayer.options.thresholds = onshoreWindThresholds;
      }
    }
    
    // Update offshore layer colors
    if (offshoreWindThresholds) {
      let offshoreLayer = null;
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.GeoJSON && layer.options.offshoreWindLayer) {
          offshoreLayer = layer;
        }
      });
      
      if (offshoreLayer) {
        offshoreLayer.eachLayer((featureLayer) => {
          if (featureLayer.feature) {
            const newStyle = getZoneStyle(featureLayer.feature, 'offshore_wind', offshoreWindThresholds, zoomLevel);
            featureLayer.setStyle(newStyle);
          }
        });
        offshoreLayer.options.thresholds = offshoreWindThresholds;
      }
    }
  }, [onshoreWindThresholds, offshoreWindThresholds]); // Only when thresholds change

  useEffect(() => {
    if (!layoutActive || !mapInstanceRef.current) return undefined;
    const id = window.requestAnimationFrame(() => {
      const map = mapInstanceRef.current;
      if (!map) return;
      map.invalidateSize();
      if (typeof map.redraw === 'function') {
        map.redraw();
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [layoutActive]);

  return (
    <div className={className}>
      <div 
        ref={mapRef}
        className="w-full h-full min-h-96"
      />
      
      {/* Loading and error overlays are handled by parent component */}
    </div>
  );
};

export default WindMap;
